import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../config/axios";
import { useRole } from "../../context/RoleContext";
import { buildTradeRecordSheetPrintHtml } from "../../utils/tradeRecordSheetPrintHtml";

function statusLabel(slot) {
  const status = slot?.status || "not_sent";
  if (status === "awaiting_signature") {
    const signers = slot.signers || [];
    const signed = Number(slot.signedCount || 0);
    const total = Number(slot.signerCount || signers.length || 0);
    if (total > 1) {
      const waiting = signers.find(
        (row) => String(row.status || "").toLowerCase() !== "signed"
      );
      const who = waiting?.name || waiting?.email || "";
      return who
        ? `Awaiting signature (${signed} of ${total}) — ${who}`
        : `Awaiting signature (${signed} of ${total})`;
    }
    const who = signers[0]?.name || signers[0]?.email || "";
    return who ? `Awaiting signature — ${who}` : "Awaiting signature";
  }
  if (status === "signed") {
    return slot?.hasSignedFile ? "Signed" : "Signed — preparing PDF…";
  }
  if (status === "declined") return "Declined";
  if (status === "error") return "Could not send";
  return "Not sent";
}

function SlotRow({ kind, label, slot, sending, refreshing, onSend, onDownload, onRefresh }) {
  const status = slot?.status || "not_sent";
  const canDownload = status === "signed" || slot?.hasSignedFile;
  const awaiting = status === "awaiting_signature";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-gray-800 w-28">{label}</span>
      <span className="text-xs text-gray-600">{statusLabel(slot)}</span>
      <button
        type="button"
        onClick={() => onSend(kind)}
        disabled={sending === kind}
        className="bg-blue-900 text-white text-xs px-3 py-1.5 rounded hover:bg-blue-800 disabled:opacity-50"
      >
        {sending === kind ? "Sending…" : `Send ${label}`}
      </button>
      {awaiting || (status === "signed" && !slot?.hasSignedFile) ? (
        <button
          type="button"
          onClick={() => onRefresh()}
          disabled={refreshing}
          className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {refreshing ? "Checking…" : "Refresh status"}
        </button>
      ) : null}
      {canDownload ? (
        <button
          type="button"
          onClick={() => onDownload(kind)}
          className="text-xs px-3 py-1.5 rounded border border-blue-900 text-blue-900 hover:bg-blue-50 font-medium"
        >
          Download signed
        </button>
      ) : null}
      {slot?.lastError ? (
        <span className="text-xs text-red-600">{slot.lastError}</span>
      ) : null}
    </div>
  );
}

const TrsSignatureActions = ({ trade, onUpdate }) => {
  const { isAgent } = useRole();
  const [sending, setSending] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [local, setLocal] = useState(trade?.trsSignatures || {});
  const tradeRef = useRef(trade);
  tradeRef.current = trade;

  useEffect(() => {
    setLocal(trade?.trsSignatures || {});
  }, [trade?._id]);

  useEffect(() => {
    if (!trade?._id || isAgent) return undefined;
    let cancelled = false;
    const applyNext = (next) => {
      setLocal((prev) =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next
      );
      const current = tradeRef.current;
      if (
        typeof onUpdate === "function" &&
        current?._id === trade._id &&
        JSON.stringify(current.trsSignatures || {}) !== JSON.stringify(next)
      ) {
        onUpdate({ ...current, trsSignatures: next });
      }
    };
    const refresh = async () => {
      try {
        const res = await axiosInstance.get(
          `/trades/${trade._id}/trs-signature`
        );
        if (cancelled) return;
        applyNext(res.data.trsSignatures || {});
      } catch (_) {}
    };
    refresh();
    const awaiting =
      local?.open?.status === "awaiting_signature" ||
      local?.closed?.status === "awaiting_signature" ||
      (local?.open?.status === "signed" && !local?.open?.hasSignedFile) ||
      (local?.closed?.status === "signed" && !local?.closed?.hasSignedFile);
    // Keep polls slow — Zoho Sign rate-limits status checks (URL_ROLLING_THROTTLES…).
    const timer = window.setInterval(refresh, awaiting ? 20000 : 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [trade?._id, isAgent, onUpdate, local?.open?.status, local?.closed?.status, local?.open?.hasSignedFile, local?.closed?.hasSignedFile]);

  if (isAgent || !trade?._id) return null;

  const send = async (kind) => {
    if (sending) return;
    setSending(kind);
    try {
      const html = await buildTradeRecordSheetPrintHtml(
        trade,
        axiosInstance,
        null
      );
      const res = await axiosInstance.post(
        `/trades/${trade._id}/trs-signature`,
        { kind, html },
        { timeout: 120000 }
      );
      const next = res.data.trsSignatures || {};
      setLocal(next);
      toast.success(res.data.message || "TRS sent for signature.");
      if (typeof onUpdate === "function") {
        onUpdate({ ...trade, trsSignatures: next });
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not send the TRS for signature."
      );
    } finally {
      setSending("");
    }
  };

  const refreshStatus = async () => {
    if (refreshing || !trade?._id) return;
    setRefreshing(true);
    try {
      const res = await axiosInstance.get(`/trades/${trade._id}/trs-signature`);
      const next = res.data.trsSignatures || {};
      setLocal(next);
      if (typeof onUpdate === "function") {
        onUpdate({ ...tradeRef.current, trsSignatures: next });
      }
      const open = next.open || {};
      const closed = next.closed || {};
      if (open.status === "signed" || closed.status === "signed") {
        toast.success("Signed TRS is ready to download.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not refresh TRS signature status."
      );
    } finally {
      setRefreshing(false);
    }
  };

  const download = async (kind) => {
    try {
      const res = await axiosInstance.get(
        `/trades/${trade._id}/trs-signature/${kind}/file`,
        { responseType: "blob", timeout: 120000 }
      );
      const type = res.headers["content-type"] || "";
      if (type.includes("application/json")) {
        const text = await res.data.text?.();
        let message = "Could not download the signed TRS.";
        try {
          message = JSON.parse(text).message || message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${kind === "closed" ? "Closed" : "Open"}-TRS-Trade-${
        trade.tradeNumber
      }-signed.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      try {
        const statusRes = await axiosInstance.get(
          `/trades/${trade._id}/trs-signature`
        );
        const next = statusRes.data.trsSignatures || {};
        setLocal(next);
        if (typeof onUpdate === "function") {
          onUpdate({ ...tradeRef.current, trsSignatures: next });
        }
      } catch (_) {}
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not download the signed TRS."
      );
    }
  };

  const open = local.open || {};
  const closed = local.closed || {};

  return (
    <div className="mb-6 p-3 border border-blue-100 bg-blue-50 rounded-md">
      <p className="text-sm font-semibold text-blue-900 mb-2">
        TRS signature (Zoho Sign)
      </p>
      <div className="flex flex-col gap-2">
        <SlotRow
          kind="open"
          label="Open TRS"
          slot={open}
          sending={sending}
          refreshing={refreshing}
          onSend={send}
          onDownload={download}
          onRefresh={refreshStatus}
        />
        <SlotRow
          kind="closed"
          label="Closed TRS"
          slot={closed}
          sending={sending}
          refreshing={refreshing}
          onSend={send}
          onDownload={download}
          onRefresh={refreshStatus}
        />
      </div>
    </div>
  );
};

export default TrsSignatureActions;
